<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, getCurrentInstance, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import FormButton from '../../Atoms/FormButton.vue'
import TabGroup from '../../Atoms/TabGroup.vue'
import TabItem from '../../Atoms/TabItem.vue'
import IconBase from '../../icon/IconBase.vue';
import IconCopy from '../../icon/IconCopy.vue';
import FormInput from '../../Atoms/FormInput.vue';
import IconEye from '../../icon/IconEye.vue';
import IconEyeCrossed from '../../icon/IconEyeCrossed.vue';
import GetRtmpSrtButtons from './GetRtmpSrtButtons.vue';
import Tooltip from '../../Atoms/Tooltip.vue'
import IconHelp from '../../icon/IconHelp.vue';
import ButtonCopyToClipboard from '../../Moleculas/ButtonCopyToClipboard.vue';
import FormSwitch from '../../Atoms/FormSwitch.vue';
import IconChevronDown from '../../icon/IconChevronDown.vue';
import StreamService from '@/services/StreamService';

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  streamAlive: {
    type: Boolean
  },
  isRtmpPullEnabled: {
    type: Boolean,
    default: false
  },
  rmptPullUrlProcessing: {
    type: Boolean,
    default: false
  },
  transcoderView: {
    type: Boolean,
    default: false
  },
  streamSourceType: {
    type: String
  },
  sourceTypes: {
    type: Object
  },
  selectedLinkedSource: {
    type: Object,
    default: null
  },
  sourceSet: {
    type: Boolean,
    default: false
  },
  getStreamPushUrl: {
    type: String
  },
  getStreamUrlAndKey: {
    type: String,
  },
  streamKey: {
    type: String
  },
  isPasswordEnabled: {
    type: Boolean,
    default: false
  },
  isAnyBackupStreamLive: {
    type: Boolean,
    default: false
  },
  streamKeySrt: {
    type: String,
    default: null
  },
  failoverIgestActive: {
    type: Boolean,
    default: false
  },
  srtRegionIngestDestination: {
    type: String,
    default: null
  },
})

const store = useStore()
const { notify } = useNotification()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const hasFailoverIngestFeature = computed(() => store.getters['User/hasFailoverIngestFeature'])

const emit = defineEmits({
  'request-url': () => true,
  'set-linked-source': () => true,
  'init-selected-source': () => true,
  'selected-source': () => true,
  'update-password-enabled': () => true,
  'set-rtmps': () => true,
})

const streamKeyVisible = ref(false)
const streamKeyVisibleTimeout = ref(0)
const streamKeyVisibleTimeoutCtrl = ref(null)
const showSRT = ref(false)
const srtEncryptionSaveProcessing = ref(false)
const srtEncryptionToggleProcessing = ref(false)
const srtEncryptionEnabled = ref(false)
const srtEncryptionKey = ref('')

watch(
  () => [
    props.stream?._id,
    props.stream?.config?.srtEncryptionEnabled,
    props.stream?.config?.srtEncryptionKey,
  ],
  () => {
    srtEncryptionEnabled.value = props.stream?.config?.srtEncryptionEnabled ?? false
    srtEncryptionKey.value = props.stream?.config?.srtEncryptionKey ?? ''
  },
  { immediate: true },
)

const activeTab = ref('rtmp')

const TAB_STORAGE_KEY = 'tabPublish_activeTab'
onMounted(() => {
  const savedTab = localStorage.getItem(TAB_STORAGE_KEY)
  if (savedTab && ['rtmp', 'rtmps', 'srt', 'whip'].includes(savedTab)) {
    activeTab.value = savedTab
    if (savedTab === 'rtmps') {
      emit('set-rtmps', true)
    } else {
      emit('set-rtmps', false)
    }
  }
})

const setActivetab = (tabName) => {
  if (tabName === 'rtmps') {
    activeTab.value = tabName
    emit('set-rtmps', true)
  } else {
     activeTab.value = tabName
    emit('set-rtmps', false)
  }
  
  localStorage.setItem(TAB_STORAGE_KEY, tabName)
}

const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value);
  notify({ group: 'info', text: 'Copied to clipboard' });
}

const toggleStreamKeyVisibility = () => {
  window.clearInterval(streamKeyVisibleTimeoutCtrl.value);
  const newState = !streamKeyVisible.value;
  streamKeyVisible.value = newState;

  if (newState) {
    const timeout = 1000;
    streamKeyVisibleTimeout.value = 9000;
    streamKeyVisibleTimeoutCtrl.value = setInterval(() => {
      streamKeyVisibleTimeout.value -= timeout;
      if (!streamKeyVisibleTimeout.value)
        toggleStreamKeyVisibility();
    }, timeout);

    // track event
    window.trackEvent('Stream Key Viewed', {
      streamId: props.stream._id,
      streamName: props.stream.name,
    });
  }
}

const SRTHostname = computed(() => {
  let streamHost = `srt://${props.srtRegionIngestDestination}.castr.io`;
  return streamHost;
})

const SRTPort = computed(() => {
  let streamPort = `9998`;
  return streamPort;
})

const SRTID = computed(() => {
  let streamIDSrt = `#!::r=${props.stream.key},m=publish`;
  if (props.stream.config && props.stream.config.password && props.isPasswordEnabled) {
    streamIDSrt = `#!::r=${props.stream.key},password=${props.stream.config.password},m=publish`
  }
  return streamIDSrt
})

const streamKeyWHIP = computed(() => {
  let hostname = _.get(props.stream, 'uiRegion.config.failover.backupIngest');
  if (!hostname) {
    hostname = _.get(props.stream, 'uiRegion.config.failover.primaryIngest');
  }
  if (!hostname) {
    // uiRegion is a UI-only property that a stream refresh (e.g. after
    // toggling backup) can drop, so fall back to the persisted region.
    hostname = _.get(props.stream, 'uiRegion.hostname') || _.get(props.stream, 'region.hostname')
  }
  let streamURL = `https://${hostname}/${props.stream.key}/whip`;
  if (props.stream.config && props.stream.config.password && props.isPasswordEnabled) {
    streamURL = `https://${hostname}/${props.stream.key}/whip?password=${props.stream.config.password}`
  }

  return streamURL;
})

const shouldShowRtmps = computed(() => {
  if (props.stream.type === 'restream') return true 
  if (props.stream.type === 'live') return true 
  if (props.stream.type === 'subsecond') return true 
  if (props.stream.type === 'transcoder') return true 
  return false
})
const requestUrl = (url) => emit('request-url', url);

const isPasswordEnabledComputed = computed({
  get() {
    return props.isPasswordEnabled
  },
  set(value) {
    console.log(value);
    emit('update-password-enabled', value);
  }
})

const onSrtEncryptionToggle = async (enabled) => {
  srtEncryptionToggleProcessing.value = true
  try {
    await StreamService.toggleSrtEncryption(props.stream._id, enabled)
    srtEncryptionEnabled.value = enabled

    notify({ group: 'success', text: `SRT encryption ${enabled ? 'enabled' : 'disabled'}` })
  } catch (e) {
    notify({
      group: 'error',
      text: (e && e.message) || 'could not toggle srt encryption ingest'
    })
  } finally {
    srtEncryptionToggleProcessing.value = false
  }
}

const onSrtEncryptionKeySet = async () => {
  srtEncryptionSaveProcessing.value = true
  try {
    await StreamService.setSrtEncryptionKey(props.stream._id, srtEncryptionKey.value);

    notify({ group: 'success', text: `SRT encryption key saved` })
  } catch (e) {
    notify({
      group: 'error',
      text: (e && e.message) || 'could not set srt encryption key'
    })
  }
  srtEncryptionSaveProcessing.value = false
}

const resetStreamPassword = () => {
  showModal('modal-reset-stream-password');
}

const shouldShowResetPassword = computed(() => {
  return !props.transcoderView && props.stream.type !== 'restream'
})

</script>
<template>
  <!-- Publish source tabs -->
  <div class="flex flex-wrap w-full mt-3">
    <label class="block mb-1 text-sm text-surface-8 w-full">Protocol</label>
    <TabGroup type="segmented" :cols="shouldShowRtmps ? 4 : 3">
      <TabItem :is-active="activeTab === 'rtmp'" @click="setActivetab('rtmp')">RTMP</TabItem>
      <TabItem v-if="shouldShowRtmps" :is-active="activeTab === 'rtmps'" @click="setActivetab('rtmps')">RTMPS</TabItem>
      <TabItem :is-active="activeTab === 'srt'" @click="setActivetab('srt')">SRT</TabItem>
      <TabItem :is-active="activeTab === 'whip'" @click="setActivetab('whip')">WHIP</TabItem>
    </TabGroup>
    <div class="w-full mt-3" v-if="activeTab === 'rtmp' || activeTab === 'rtmps'">
      <div class="bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
        <div>
          <label class="block mb-1 text-sm text-surface-8">{{ failoverIgestActive && hasFailoverIngestFeature ? 'Primary Ingest URL' : 'Ingest URL' }}</label>
          <div class="flex relative w-full">
            <FormInput customClasses="!pr-8" :modelValue="getStreamPushUrl" @click="clipboardCopy(getStreamPushUrl)"
              readonly />
            <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="getStreamPushUrl" />
          </div>
        </div>
        <div>
          <div class="flex flex-wrap items-center mb-1">
            <label class="mr-auto mb-0 text-sm text-surface-8">Stream Key</label>
            <Tooltip v-if="shouldShowResetPassword" :active="streamAlive" content="Password cannot be reset as stream is live now">
              <FormButton customClasses="text-surface-8 hover:text-surface-9 text-sm disabled:text-surface-7"
                :disabled="streamAlive" size="sm" type="link" @click="resetStreamPassword()">
                Reset Password
              </FormButton>
            </Tooltip>
          </div>
          <div class="flex relative w-full">
            <FormInput customClasses="!pr-16" v-if="streamKeyVisible" class="w-[1%] flex-grow" :modelValue="streamKey"
              @click="clipboardCopy(streamKey)" readonly />
            <FormInput customClasses="!pr-12" v-if="!streamKeyVisible" class="w-[1%] flex-grow"
              :modelValue="'xxxxxxxxxxxxxxxxx'" readonly />
            <FormButton class="absolute right-7 top-px z-10" isLink isIcon type="link" size="sm"
              @click="toggleStreamKeyVisibility">
              <span v-if="streamKeyVisible" class="show-counter absolute text-c-dark-7 text-xs">({{
                streamKeyVisibleTimeout / 1000 }})</span>
              <icon-base class="" fill="none">
                <Icon-eye v-if="!streamKeyVisible" />
                <Icon-eye-crossed v-else />
              </icon-base>
            </FormButton>
            <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="streamKey" />
          </div>
        </div>
        <div v-if="stream.type === 'live' && !transcoderView" class="flex items-center justify-between gap-x-2">
          <span class="inline-flex items-center gap-1.5 text-sm text-surface-8">
            Append password to key
            <Tooltip :content="isAnyBackupStreamLive && hasFailoverIngestFeature ? 'Turn off the stream to change this' : `If your encoder doesn't support passing query parameters, turn this off.`">
              <icon-base class="text-surface-7" fill="none"><icon-help /></icon-base>
            </Tooltip>
          </span>
          <FormSwitch class="password-toggle !block" v-model="isPasswordEnabledComputed" :disabled="isAnyBackupStreamLive" />
        </div>
      </div>
      <FormButton @click="clipboardCopy(getStreamUrlAndKey)" type="secondary" size="md"
        class="mt-3 w-full justify-center sm:hidden">
        Copy Stream URL & Key as One URL
      </FormButton>
    </div>
    <div class="w-full mt-3" v-if="activeTab === 'srt'">
      <div class="bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
        <div>
          <label class="block mb-1 text-sm text-surface-8">SRT Ingest URL</label>
          <div class="flex relative w-full">
            <FormInput customClasses="!pr-8" :modelValue="streamKeySrt" @click="clipboardCopy(streamKeySrt)" readonly />
            <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="streamKeySrt" />
          </div>
        </div>
        <div v-if="stream.type === 'live' && !transcoderView" class="flex items-center justify-between gap-x-2">
          <span class="inline-flex items-center gap-1.5 text-sm text-surface-8">
            Append password to URL
            <Tooltip :content="isAnyBackupStreamLive && hasFailoverIngestFeature ? 'Turn off the stream to change this' : `If your encoder doesn't support passing query parameters, turn this off.`">
              <icon-base class="text-surface-7" fill="none"><icon-help /></icon-base>
            </Tooltip>
          </span>
          <FormSwitch class="password-toggle !block" v-model="isPasswordEnabledComputed" :disabled="isAnyBackupStreamLive" />
        </div>
      </div>

      <div class="mt-3 bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
        <div class="flex items-center justify-between gap-x-2">
          <label class="mb-0 text-sm text-surface-9">Encrypted SRT</label>
          <FormSwitch
            class="!block"
            :model-value="srtEncryptionEnabled"
            :disabled="srtEncryptionToggleProcessing"
            size="md"
            @update:model-value="onSrtEncryptionToggle"
          />
        </div>
        <div v-if="srtEncryptionEnabled">
          <label class="block mb-1 text-sm text-surface-8">Passphrase</label>
          <div class="flex items-center justify-between w-full gap-x-2">
            <FormInput customClasses="!pr-16" v-model="srtEncryptionKey" />
            <FormButton size="sm"
              :disabled="!srtEncryptionKey
              || srtEncryptionSaveProcessing
              || (srtEncryptionKey.length < 10)
              || (srtEncryptionKey.length > 80)"
              @click="onSrtEncryptionKeySet">
              {{ srtEncryptionSaveProcessing ? 'Saving...' : 'Save' }}
            </FormButton>
          </div>
          <p class="text-s-xs text-surface-7 italic mt-1 mb-0">max. 80 characters and min. 10 characters</p>
        </div>
      </div>
    </div>
    <div class="w-full mt-3" v-if="activeTab === 'whip'">
      <div class="bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3">
        <div>
          <label class="block mb-1 text-sm text-surface-8">WHIP Ingest URL</label>
          <div class="flex relative w-full">
            <FormInput customClasses="!pr-16" :modelValue="streamKeyWHIP" @click="clipboardCopy(streamKeyWHIP)"
              readonly />
            <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="streamKeyWHIP" />
          </div>
        </div>
        <div v-if="stream.type === 'live' && !transcoderView" class="flex items-center justify-between gap-x-2">
          <span class="inline-flex items-center gap-1.5 text-sm text-surface-8">
            Append password to URL
            <Tooltip :content="isAnyBackupStreamLive && hasFailoverIngestFeature ? 'Turn off the stream to change this' : `If your encoder doesn't support passing query parameters, turn this off.`">
              <icon-base class="text-surface-7" fill="none"><icon-help /></icon-base>
            </Tooltip>
          </span>
          <FormSwitch class="password-toggle !block" v-model="isPasswordEnabledComputed" :disabled="isAnyBackupStreamLive" />
        </div>
      </div>
    </div>
    <GetRtmpSrtButtons v-if="!transcoderView" @request-url="requestUrl" :stream="stream" :stream-alive="streamAlive"
      :is-rtmp-pull-enabled="isRtmpPullEnabled" :rmpt-pull-url-processing="rmptPullUrlProcessing"
      :stream-source-type="streamSourceType" :source-types="sourceTypes" class="!justify-start" />

    <div class="w-full mt-2 bg-surface-1 rounded-lg p-3 flex flex-col gap-y-3" v-if="showSRT && activeTab === 'srt'">
      <div>
        <label class="block mb-1 text-sm text-surface-8">Hostname</label>
        <div class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="SRTHostname" @click="clipboardCopy(SRTHostname)" readonly />
          <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="SRTHostname" />
        </div>
      </div>
      <div>
        <label class="block mb-1 text-sm text-surface-8">Port</label>
        <div class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="SRTPort" @click="clipboardCopy(SRTPort)" readonly />
          <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="SRTPort" />
        </div>
      </div>
      <div>
        <label class="block mb-1 text-sm text-surface-8">Stream ID</label>
        <div class="flex relative w-full">
          <FormInput customClasses="!pr-8" :modelValue="SRTID" @click="clipboardCopy(SRTID)" readonly />
          <ButtonCopyToClipboard class="absolute right-0 top-px z-10" :text="SRTID" />
        </div>
      </div>
    </div>
    <FormButton v-if="activeTab === 'srt'"
      customClasses="flex items-center justify-center text-sm text-surface-8 hover:text-white w-full gap-x-2 mt-3"
      @click="showSRT = !showSRT">
      {{ showSRT ? 'Hide ' : 'Show ' }} Separate Fields
      <icon-base :class="showSRT ? 'rotate-180' : 'rotate-0'">
        <Icon-chevron-down />
      </icon-base>
    </FormButton>
    <FormButton v-if="stream.type !== 'subsecond'"
      class="gap-x-2 !text-surface-7 hover:!text-white justify-start !px-0 mt-3"
      href="https://docs.castr.com/en/articles/4825093-recommended-settings-for-encoders" target="_blank" type="link">
      <icon-base fill="none">
        <icon-help />
      </icon-base>
      Recommended settings
    </FormButton>
  </div>
</template>